import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface Survey {
  id: string;
  title: string;
  description?: string;
  is_published: boolean;
  slug: string;
  created_at: string;
  total_responses?: number;
}

export const SurveyList: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      console.log('[SurveyList] Loading surveys from:', `${API_BASE_URL}/public/surveys`);
      const response = await axios.get(`${API_BASE_URL}/public/surveys`);
      console.log('[SurveyList] Response received:', response);
      
      // Handle both array response and wrapped response formats
      const data = Array.isArray(response.data) ? response.data : response.data.value || [];
      
      // Remove duplicates based on survey ID
      const uniqueSurveys = data.reduce((acc: Survey[], current: Survey) => {
        const isDuplicate = acc.some(survey => survey.id === current.id);
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, []);
      
      console.log('[SurveyList] Loaded surveys:', uniqueSurveys.length);
      setSurveys(uniqueSurveys);
      setLoading(false);
    } catch (err: any) {
      console.error('[SurveyList] Error loading surveys:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        url: `${API_BASE_URL}/public/surveys`
      });
      setError(err.response?.data?.message || err.message || 'Failed to load surveys');
      setLoading(false);
    }
  };

  const handleSurveyClick = (slug: string) => {
    navigate(`/survey/${slug}`);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading surveys...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <h2 style={styles.errorTitle}>Error</h2>
          <p style={styles.errorMessage}>{error}</p>
          <button onClick={loadSurveys} style={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (surveys.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyCard}>
          <h2 style={styles.emptyTitle}>No Surveys Available</h2>
          <p style={styles.emptyMessage}>
            There are currently no published surveys. Please check back later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📋 Available Surveys</h1>
        <p style={styles.subtitle}>
          Select a survey below to get started
        </p>
      </div>

      <div style={styles.surveyGrid}>
        {surveys.map((survey) => (
          <div
            key={survey.id}
            style={styles.surveyCard}
            onClick={() => handleSurveyClick(survey.slug)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
            }}
          >
            <div style={styles.surveyHeader}>
              <h3 style={styles.surveyTitle}>{survey.title}</h3>
              {survey.is_published && (
                <span style={styles.badge}>Published</span>
              )}
            </div>

            {survey.description && (
              <p style={styles.surveyDescription}>{survey.description}</p>
            )}

            <div style={styles.surveyFooter}>
              <button style={styles.takeButton}>
                Take Survey →
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: 'var(--bg-primary)',
    padding: '40px 20px',
  },
  backButton: {
    padding: '12px 24px',
    background: 'var(--accent)',
    color: 'var(--button-text)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '20px',
    maxWidth: '1400px',
    margin: '0 auto 20px',
    display: 'block',
  },
  header: {
    textAlign: 'center' as const,
    marginBottom: '40px',
    maxWidth: '800px',
    margin: '0 auto 40px',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: 'var(--text-primary)',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '1.1rem',
    color: 'var(--text-secondary)',
  },
  surveyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  surveyCard: {
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: '12px',
    padding: '24px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  surveyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    gap: '12px',
  },
  surveyTitle: {
    fontSize: '1.4rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: '0',
    flex: 1,
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
    background: 'var(--success)',
    color: 'var(--button-text)',
    flexShrink: 0,
  },
  surveyDescription: {
    fontSize: '1rem',
    color: 'var(--text-secondary)',
    marginBottom: '20px',
    lineHeight: '1.6',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as any,
    overflow: 'hidden',
  },
  surveyFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  takeButton: {
    padding: '10px 24px',
    background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%)',
    color: 'var(--button-text)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid var(--card-border)',
    borderTop: '4px solid var(--accent)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '20px',
    fontSize: '1.1rem',
    color: 'var(--text-secondary)',
  },
  errorCard: {
    maxWidth: '500px',
    margin: '100px auto',
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center' as const,
  },
  errorTitle: {
    fontSize: '1.8rem',
    color: 'var(--error)',
    marginBottom: '16px',
  },
  errorMessage: {
    fontSize: '1rem',
    color: 'var(--text-secondary)',
    marginBottom: '24px',
  },
  retryButton: {
    padding: '12px 32px',
    background: 'var(--accent)',
    color: 'var(--button-text)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  emptyCard: {
    maxWidth: '500px',
    margin: '100px auto',
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: '12px',
    padding: '40px',
    textAlign: 'center' as const,
  },
  emptyTitle: {
    fontSize: '1.8rem',
    color: 'var(--text-primary)',
    marginBottom: '16px',
  },
  emptyMessage: {
    fontSize: '1rem',
    color: 'var(--text-secondary)',
    lineHeight: '1.6',
  },
};
